import type { MutableRefObject } from "react";
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import React, { useRef, useState } from "react";
import { t } from "i18next";
import Cookies from "js-cookie";
import { Root, SettingsDropdownBody } from "./style";
import type { SolutionColors } from "@smartsuite/react-ui";
import {
  DropdownPanel,
  EmptyButton,
  PlainTextField,
  RoundButton,
} from "@smartsuite/react-ui";
import { ImportType } from "../../src/stores/config.store";

export interface SetupSettings {
  accountId: string;
  role: string;
  applicationId: string | undefined;
  solutionId: string | undefined;
  memberId: string | undefined;
  accessToken: string;
  lang: string;
  logoColor: SolutionColors;
  smartsuiteBackendUrl: string;
  importType: ImportType;
}

export const SetupDropdown: React.FC<{
  value: SetupSettings;
  setValue: (settings: SetupSettings) => void;
  openAutomation: () => void;
}> = ({ value, setValue, openAutomation }) => {
  const rootRef = useRef(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const [localValue, setLocalValue] = useState<SetupSettings>(value);

  const onSubmit = (): void => {
    setModalOpen(false);
    Cookies.set("accessToken", localValue.accessToken, {
      domain: ".smartsuite.com",
    });
    if (
      value.accountId !== localValue.accountId ||
      value.role !== localValue.role ||
      value.accessToken !== localValue.accessToken ||
      value.applicationId !== localValue.applicationId ||
      value.lang !== localValue.lang ||
      value.smartsuiteBackendUrl !== localValue.smartsuiteBackendUrl ||
      value.importType !== localValue.importType ||
      value.solutionId !== localValue.solutionId ||
      value.memberId !== localValue.memberId
    ) {
      setValue(localValue);
    }
  };

  return (
    <Root ref={rootRef} isModalOpen={isModalOpen} mainColor="black">
      <div className="head">
        <EmptyButton
          ref={buttonRef}
          size="lg"
          style={{ color: "currentColor" }}
          onClick={() => setModalOpen((isOpen) => !isOpen)}
        >
          {t("setup-dropdown.settings") ?? "Settings"}
        </EmptyButton>
      </div>

      <DropdownPanel
        targetElementRef={buttonRef as MutableRefObject<HTMLElement>}
        isActive={isModalOpen}
        minWidth={300}
        onClickOutside={() => setModalOpen(false)}
        zIndex={1002}
      >
        <SettingsDropdownBody>
          <PlainTextField
            label="Account ID"
            name="account-id"
            placeholder=""
            value={localValue.accountId}
            onChange={(e) =>
              setLocalValue({ ...localValue, accountId: e.target.value })
            }
          />

          <PlainTextField
            label="Role"
            name="role"
            placeholder=""
            value={localValue.role}
            onChange={(e) =>
              setLocalValue({ ...localValue, role: e.target.value })
            }
          />

          <PlainTextField
            label="Application ID"
            name="application-id"
            placeholder=""
            value={localValue.applicationId}
            onChange={(e) =>
              setLocalValue({ ...localValue, applicationId: e.target.value })
            }
          />
          <PlainTextField
            label="Solution ID"
            name="solution-id"
            placeholder=""
            value={localValue.solutionId}
            onChange={(e) =>
              setLocalValue({ ...localValue, solutionId: e.target.value })
            }
          />
          <PlainTextField
            label="Member ID"
            name="member-id"
            placeholder=""
            value={localValue.memberId}
            onChange={(e) =>
              setLocalValue({ ...localValue, memberId: e.target.value })
            }
          />

          <PlainTextField
            label="Access Token"
            name="access-token"
            placeholder=""
            value={localValue.accessToken}
            onChange={(e) =>
              setLocalValue({ ...localValue, accessToken: e.target.value })
            }
          />
          <PlainTextField
            label="SmartSuite backend URL"
            name="smartsuite-backend-url"
            placeholder=""
            value={localValue.smartsuiteBackendUrl}
            onChange={(e) =>
              setLocalValue({
                ...localValue,
                smartsuiteBackendUrl: e.target.value,
              })
            }
          />
          <div>
            <label htmlFor="import-type">Import type</label>
            <br />
            <select
              name="import-type"
              value={localValue.importType}
              onChange={(e) =>
                setLocalValue({
                  ...localValue,
                  importType: e.target.value as ImportType,
                })
              }
            >
              <option>{ImportType.application}</option>
              <option>{ImportType.solution}</option>
            </select>
          </div>

          <PlainTextField
            label="Language"
            name="lang"
            placeholder=""
            value={localValue.lang}
            onChange={(e) =>
              setLocalValue({ ...localValue, lang: e.target.value })
            }
          />

          <div className="footer" style={{ gap: "5px" }}>
            <RoundButton size="lg" onClick={() => onSubmit()}>
              {t("general.save")}
            </RoundButton>
            <RoundButton
              size="lg"
              onClick={() => {
                openAutomation();
                setModalOpen(false);
              }}
            >
              Open Import
            </RoundButton>
          </div>
        </SettingsDropdownBody>
      </DropdownPanel>
    </Root>
  );
};
